import { useCallback, useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import { Trans, useTranslation } from 'next-i18next'
import styled from 'styled-components'
import {
  any,
  anyPass,
  equals,
  isEmpty,
  keys,
  map,
  or,
  path,
  pick,
  pipe,
  values
} from 'ramda'
import {
  Box,
  Button,
  Card,
  IconArrowUpRight,
  ResponsiveRender,
  Stack,
  Text,
  TextLink,
  TextLinkButton,
  useDialogControllers,
  useToast
} from '@machobear/design-system'
import toNumber from 'lodash/toNumber'
import posthog from 'posthog-js'
import { useDispatch, useSelector } from 'react-redux'

import ENV_VARS from '@/config'
import structure from '@/structure'
import { breakpoints, colors, fontWeights, zIndex } from '@/styles'
import { useCityData } from '@/lib/locations'
import { useStore } from '@/lib/redux'
import { headObj } from '@/utils'
import { getLocalBuildingUrl } from '@/utils/building'
import { formatSize } from '@/utils/format-size'
import { getImageUrl } from '@/utils/image'
import {
  getPaymentFrequency,
  getUrl,
  isGroup as isGroupGlobal,
  isW1 as isW1Global
} from '@/utils/listings'
import { getCanonicalUrl } from '@/utils/seo'
import { useSignupMessage } from '@/hooks/signup/use-signup-message'
import useCurrentSession from '@/hooks/use-current-session'
import { useEnsureLoggedIn } from '@/hooks/use-ensure-loggedIn'
import useMediaQuery from '@/hooks/use-media-query'
import useSessionComplete from '@/hooks/use-session-complete'
import useSetTrrebDlv from '@/hooks/use-set-trreb-dlv'
import { show as showLoginModal } from '@/features/Auth/login-modal'
import ErrorPage from '@/features/ErrorPage'
import BuildingInfo from '@/features/ListingDetail/building-info'
import DesktopHeader from '@/features/ListingDetail/desktop-header'
import {
  LeaseDetails,
  LeaseDetailsBlurred
} from '@/features/ListingDetail/lease-details'
import ListingAmenities from '@/features/ListingDetail/listing-amenities'
import {
  RentedNotification,
  StatusBanner,
  WarningNotification
} from '@/features/ListingDetail/listing-banners'
import ListingContact from '@/features/ListingDetail/listing-contact'
import ListingDescription from '@/features/ListingDetail/listing-description'
import ListingSummary from '@/features/ListingDetail/listing-summary'
import ListingTour from '@/features/ListingDetail/listing-tour'
import { show as showReportListingModal } from '@/features/ListingDetail/report-listing-modal'
import { show as showShareModal } from '@/features/ListingDetail/share-modal'
import SimilarListings from '@/features/ListingDetail/similar-listings'
import { show as showAppointmentFormModal } from '@/features/Listings/appointment-form'
import useListingsFavourites from '@/features/Listings/hooks/use-listings-favourites'
import { useMessageModalContext } from '@/features/ListingsMap/map-list/context'
import ApplyNowStepModal from '@/components/Apply/ApplyNowStepModal'
import { CityBreadcrumbs } from '@/components/breadcrumbs'
import CityLinkList from '@/components/city-link-list'
import { WarningModal } from '@/components/ds-warning-modal'
import Head from '@/components/head'
import { FlagIcon } from '@/components/icons'
import {
  InternalNavigation,
  NavigationSection
} from '@/components/internal-navigation'
import { getListingTypeText } from '@/components/listing-card-new/utils/listingTypeText'
import ListingSection from '@/components/listing-section'
import { LoadingDotCircle as LoadingCircle } from '@/components/loading-circle'
import PointsOfInterest from '@/components/points-of-interest'
import { useTermsDialogContext } from '@/components/terms-dialog/context'
import { toast } from '@/components/toast-notifications'

import { useDSLogin } from '../Auth/Context/ds-login-provider'
import DSLoginModal from '../Auth/login-modal/ds-login-modal'
import ListingImages from './listing-images'
import LoginRequiredCard from './login-required-card'
import SpecialOffer from './special-offer'
import TermsRequiredCard from './terms-required-card'

const { DASHBOARD_URL } = ENV_VARS

const TRREB_TAG = 'TRREB'
export const IMPORTED_LISTING_PLATFORM = [TRREB_TAG]

const equalsOne = equals('1')
const isTrue = anyPass([equals('1'), equals(1), equals(true)])

const sidePanelBreakpoint = '1149px'

const DisclaimerContainer = styled.div`
  display: flex;
  width: 95%;
  max-width: 1096px;
  margin: 0 auto;
  padding-top: 50px;
  padding-bottom: 30px;

  @media (max-width: ${sidePanelBreakpoint}) {
    max-width: 672px;
  }
`

const LoadingDiv = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: fixed;
  height: 100vh;
  margin-top: 0;
  width: 100%;
  background-color: rgba(255, 255, 255, 1);
  z-index: ${zIndex.loading};
`

const Disclaimer = styled.p`
  margin: 0;
  color: ${colors.darkGrey};
  font-size: 12px;
  font-style: italic;
  font-weight: ${fontWeights.medium};
  letter-spacing: 0.21px;
  line-height: 17px;
`

const NavBarButtonPlacement = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
  max-width: ${structure.page.width + 40}px;
  margin: 0 auto;
  padding: 0 20px;

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    width: auto;
    display: flex;
    justify-content: space-between;
  }

  @media screen and (max-width: 350px) {
    margin: 0 10px;
  }
`

const ListingInfo = styled(InternalNavigation)`
  position: relative;
  display: flex;
  width: 100%;
  min-height: 400px;
  margin-right: auto;
  margin-left: auto;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
`

const ListingInfoContent = styled.div`
  font-family: var(--font-open-sans, 'Open Sans', sans-serif);
  display: flex;
  width: 100%;
  justify-content: center;
  max-width: ${structure.desktop.page + structure.desktop.gutter * 2}px;
`

const ListingInfoLeft = styled.div`
  margin-right: ${structure.desktop.gutter}px;
  flex: 1 1 auto;

  @media (max-width: ${sidePanelBreakpoint}) {
    margin-right: auto;
    margin-left: auto;
  }

  @media (max-width: 715px) {
    width: 100%;
  }
`

const StyledListingSection = styled(ListingSection)`
  margin-top: 0px;
`

const ListingInfoRight = styled.section`
  flex: 0 0 ${structure.desktop.rightPanel}px;

  @media (max-width: ${sidePanelBreakpoint}) {
    display: none;
  }
`

const StickyContentWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    margin: 24px 0;
  }
`

const ReportListingButton = styled.button`
  color: ${colors.red};
  border: 1px solid ${colors.grey88};
  border-radius: 6px;
  cursor: pointer;
  min-width: 188px;
  padding: 6px 10px;
  font-weight: ${fontWeights.medium};
  background: transparent;
`

const ClickWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  pointer-events: none;
`

const TabletButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
  padding: 12px 16px;
  width: 100%;
`

const TabletApplyFooterWrapper = styled.div`
  display: none;
  @media (max-width: ${sidePanelBreakpoint}) {
    display: block;
    position: fixed;
    left: 0px;
    right: 0px;
    bottom: 0px;
    border-top: 1px solid ${colors.grey88};
    background-color: ${colors.background};
    z-index: 125;
    padding-right: ${props => (props.$isIpadMini ? '32px' : '16px')};
    padding-left: ${props => (props.$isIpadMini ? '32px' : '16px')};
  }
`

const TabletApplyFooterCenter = styled.div`
  position: static;
  left: 0px;
  right: 0px;
  bottom: 0px;
  display: block;
`

const TabletApplyFooterContainer = styled.div`
  position: static;
  left: 0px;
  right: 0px;
  bottom: 0px;
  z-index: 6;
  display: flex;
  padding: 16px 0;
  justify-content: space-between;
  background-color: transparent;

  @media (max-width: ${breakpoints.phoneMax}) {
    padding: 0;
  }
`

const ListingDetailsTitle = styled.h2`
  margin: 0 0 32px;
  font-size: 24px;
  line-height: 32px;
`

const StyledNavigationSection = styled(NavigationSection)`
  display: flex;
  justify-content: center;
  width: 100%;
  margin: 55px 0;
`

const renderDisclaimer = companyName => (
  <DisclaimerContainer>
    <Disclaimer>
      <Trans
        i18nKey="listing:b.listing.view.disclaimer.text"
        values={{ companyName }}
        defaults="**Disclaimer: We do not guarantee any move-in dates at this time.
        The above information, while deemed to be correct, is not guaranteed.
        Leasing availability, rates and timing are subject to
        change. {{companyName}} provides professional property
        management services on behalf of the owners of these properties.
        Tenants are not required to pay any fees to {{companyName}} for the leasing
        of these properties. Photos are of display suites only. Actual views
        and finishings may vary from what's shown. Display suite furniture not
        included. Furnished suites are not guaranteed at this time. All
        illustrations and renderings reflect the artists' interpretation of the
        project only and do not take into account the neighbouring buildings,
        physical structures, streets and landscapes. Not all amenities may be
        available upon completion."
      />
    </Disclaimer>
  </DisclaimerContainer>
)

const InternalNavBarButtons = ({ children }) => {
  return <NavBarButtonPlacement>{children}</NavBarButtonPlacement>
}

const ListingDetailPage = ({ poiData }) => {
  const { t } = useTranslation()
  const store = useStore()
  const dispatch = useDispatch()
  const router = useRouter()
  const showToast = useToast()
  const isLoggedIn = useSessionComplete()
  const { setAfterLogin } = useDSLogin()
  const { listings: favourites } = useListingsFavourites()
  const { showMessageModal } = useMessageModalContext()
  const { isLoading, showModalForRequiredTerms, checkAgreementMismatch } =
    useTermsDialogContext()
  const { visible, showModal, closeModal } = useDialogControllers({
    loginModal: false,
    landlordWarning: false,
    apply: false
  })
  const ensureLoggedIn = useEnsureLoggedIn({
    showLogin: showModal('loginModal')
  })

  const isSidePanelHidden = useMediaQuery(`(max-width: ${sidePanelBreakpoint})`)
  const isDesktop = useMediaQuery(`(min-width: ${breakpoints.desktop})`)
  const isTabletPortrait = useMediaQuery(
    `(min-width: ${breakpoints.tabletPortrait})`
  )
  const isIpadMini = useMediaQuery(`(min-width: ${breakpoints.ipadMiniMax})`)

  const [showTopNav, setShowTopNav] = useState(true)
  const [isWarningOpen, setIsWarningOpen] = useState(true)

  const users = useSelector(path(['listing', 'users']))
  const companies = useSelector(path(['listing', 'companies']))
  const broker = useSelector(store.select.listing.broker)
  const listingLinks = useSelector(store.select.listing.listingLinks)
  const unit = useSelector(path(['listing', 'unit']))
  const buildingImages = useSelector(store.select.listing.buildingImages)
  const unitImages = useSelector(store.select.listing.unitImages)
  const unitVideos = useSelector(store.select.listing.unitVideos)
  const userImage = useSelector(store.select.listing.userImage)
  const similarListings = useSelector(store.select.listing.similarListings)
  const appointments = useSelector(store.select.listing.appointments)
  const building = useSelector(path(['listing', 'building']))
  const listings = useSelector(store.select.listing.listingDetail)
  const session = useCurrentSession()
  const loading = useSelector(path(['loading', 'semaphore']))

  const { city: citySlug } = router.query
  const { data: cityData, loading: cityDataLoading } = useCityData(citySlug)

  const activated = router?.query?.activated
  useEffect(() => {
    if (activated === 'true') {
      toast(
        t(
          'listing:b.listing.activate.success.message',
          'Your listing has been successfully reactivated!'
        ),
        {
          title: t(
            'listing:b.listing.activate.success.title',
            'Listing Activated'
          ),
          type: 'success'
        }
      )
    } else if (activated === 'false') {
      toast(
        t(
          'listing:b.listing.activate.error.message',
          'Oops! Something went wrong while reactivating your listing.'
        ),
        {
          title: t(
            'listing:b.listing.activate.error.title',
            'Listing Activation Failed'
          ),
          type: 'error'
        }
      )
    }
  }, [activated, t])

  useSignupMessage()

  const isAgreementMismatched = checkAgreementMismatch('trreb_agreements')
  const isAgreementLoading = isLoading && isLoggedIn
  const isImported = listings?.tags?.some(tag =>
    IMPORTED_LISTING_PLATFORM.includes(tag)
  )
  const isListingLocked =
    isImported && (isAgreementMismatched || !isLoggedIn || isAgreementLoading)
  useSetTrrebDlv(isAgreementMismatched)

  const isHouse = useCallback(() => {
    return building.building_type_txt_id === 'house'
  }, [building])
  const isStreetNumberHidden = useCallback(() => {
    return equalsOne(unit.hide_unit_number) && isHouse()
  }, [unit, isHouse])
  const unitType = useCallback(() => {
    const TYPES = {
      condo: t('common.apartment', 'Apartment'),
      house: t('common.house', 'House'),
      townhouse: t('common.townhouse', 'Townhouse')
    }
    const scope = unit.unit_type_scope_txt_id
    if (scope === 'single' || scope === 'partial') {
      const listingType = unit
        ? getListingTypeText(
            {
              unitType: unit.unit_type_txt_id,
              unitScope: unit.unit_type_scope_txt_id,
              unitSubtype: unit.unit_subtype_txt_id
            },
            t
          )
        : ''
      return listingType
    }
    return TYPES[unit.unit_type_txt_id]
  }, [t, unit])
  const getBedCount = useCallback(() => {
    const count = toNumber(unit.count_bedrooms)
    if (count === 0) return t('common.studio', 'Studio')
    return t('listing:b.listing.view.count_bedrooms.text', {
      defaultValue: '{{count}} Bedroom',
      defaultValue_plural: '{{count}} Bedrooms',
      count
    })
  }, [t, unit?.count_bedrooms])

  if (
    (loading > 0 && isImported) ||
    (isLoggedIn && isImported && isAgreementLoading)
  ) {
    return (
      <LoadingDiv>
        <LoadingCircle />
      </LoadingDiv>
    )
  }

  // Render "Listing no longer available" error page if no listing data
  const renderError = () => <ErrorPage type={ErrorPage.Types.NotAvailable} />
  if (!listings) return renderError()

  const allowApplications = headObj(companies)?.allow_applications === '1'

  const isRented =
    listings?.state_machine === 'rented' || isTrue(listings?.marked_as_rented)

  const isApartmentBuilding =
    building.building_type_txt_id === 'highrise' ||
    building.building_type_txt_id === 'lowrise'
  const buildingUrl = isApartmentBuilding
    ? getLocalBuildingUrl(building, router.locale)
    : null

  const listingLandlords = listings?.listing_landlords ?? []
  const isListingLandlord = listingLandlords?.includes(session?.id)

  const hasSession = !!session?.id && session.registration_complete === '1'
  const isLandlord = hasSession && session.user_type !== 'tenant'

  // Tracking IDs
  const inquireClickEventId = hasSession
    ? 'logged-message-click'
    : 'listing-message-click'
  const applyClickEventId = hasSession
    ? `logged-apply-click-${listings.id}`
    : `listing-apply-click-${listings.id}`

  const getLandlord = () => {
    const landlordId = Number(listings.primary_landlord)
    const landlord = users[landlordId]
    return {
      ...landlord,
      picture: userImage?.aws_s3_key,
      company: companies?.[landlord?.company_id] || {},
      broker
    }
  }

  const getRentInfo = () => {
    const { price, price_frequency } = listings

    const freq = getPaymentFrequency(price_frequency).toLowerCase()
    if (isGroup()) {
      return ` starting at $${unit.gr_min_price}/${freq}`
    }
    const formattedPrice = `$${toNumber(price)}`
    return toNumber(price) > 0 ? ` for ${formattedPrice}/${freq}` : ''
  }
  const getTitle = () => {
    const { full_street_name, street_name, city, state } = building
    const { furnished } = listings

    const count = toNumber(unit.count_bedrooms)
    const bedCount =
      count === 0
        ? `${t('common.studio', 'Studio')} `
        : `${count} ${t('common.bedroom', 'Bedroom')} `
    const getIsFurnished = () => {
      if (furnished === null) return ''
      return equalsOne(furnished)
        ? `${t('common.furnished', 'Furnished')} `
        : `${t('common.unfurnished', 'Unfurnished')} `
    }
    const preText =
      unit.unit_type_scope_txt_id === 'single' ? getIsFurnished() : bedCount
    const type = unitType()
    const unitNumber = getUnitNumber() ? `${getUnitNumber()} - ` : ''
    return isStreetNumberHidden()
      ? `${street_name}, ${city}, ${state} - ${preText}${type} ${t(
          'common.for_rent',
          'for Rent'
        )} | liv.rent`
      : `${unitNumber}${full_street_name}, ${city}, ${state} - ${preText}${type} ${t(
          'common.for_rent',
          'for Rent'
        )} | liv.rent`
  }
  const getDescription = () => {
    const { city, state } = building
    const { furnished } = listings

    const count = toNumber(unit.count_bedrooms)
    const bedCount =
      count === 0
        ? `${t('common.studio', 'Studio')} `
        : `${count} ${t('common.bedroom', 'Bedroom')} `

    const getIsFurnished = () => {
      if (furnished === null) return ''
      return equalsOne(furnished)
        ? `${t('common.furnished', 'Furnished')} `
        : `${t('common.unfurnished', 'Unfurnished')} `
    }
    const furnishedText = getIsFurnished()
    const bedroomText = unit.unit_type_scope_txt_id === 'single' ? '' : bedCount
    const type = unitType()
    const locationText = `${city}, ${state}`
    const landlordInfo = getLandlord()
    const externalInfo = listings.external_info
    const displayExternalRealtorInfo = externalInfo.realtor_display_info
    const landlordName = displayExternalRealtorInfo
      ? externalInfo.realtor_name
      : landlordInfo.company.name
    const rentInfo = getRentInfo()

    const descriptionText =
      `${furnishedText}${bedroomText}${type} in ${locationText}` +
      `${landlordName ? ` by ${landlordName}` : rentInfo}. ` +
      `Explore listing, view photos, and contact landlord easily on liv.rent.`

    return descriptionText
  }
  const getListingUrl = () => {
    const listingUrl = getUrl({
      building_type: building.building_type_txt_id,
      listing_id: listings.id,
      city: building.city
    })
    return listingUrl
  }
  const getBuildingType = () => {
    const unitScope = unit.unit_type_scope_txt_id
    const unitType = unit.unit_type_txt_id
    if (unitScope === 'single') return 'Room'
    else if (unitType === 'condo') return 'ApartmentComplex'
    else if (unitType === 'house') return 'House'
    else return 'Accommodation'
  }
  const getListingType = () => {
    const unitScope = unit.unit_type_scope_txt_id
    const unitType = unit.unit_type_txt_id
    if (unitScope === 'single') return 'Suite'
    else if (unitType === 'condo') return 'Apartment'
    else if (unitType === 'house') return 'SingleFamilyResidence'
    else return 'SingleFamilyResidence'
  }
  const getListingName = () => {
    const unitScope = unit.unit_type_scope_txt_id
    const unitType = unit.unit_type_txt_id
    const addressText = `for Rent at ${
      isStreetNumberHidden() ? building.street_name : building.full_street_name
    }, ${building.city}, ${building.state}`
    if (unitScope === 'single') return `Room ${addressText}`
    else if (unitType === 'condo') return `Apartments ${addressText}`
    else if (unitType === 'house') return `House ${addressText}`
    else return `Townhouse ${addressText}`
  }
  const getListingSchema = () => {
    const { allow_dogs, allow_cats, max_occupancy, price } = listings
    const {
      city,
      full_street_name,
      full_address,
      street_name,
      lat,
      lng,
      zip,
      state,
      country_code
    } = building
    const { setting_size_unit } = session

    const buildingType = getBuildingType()
    const listingType = getListingType()
    const landlord = getLandlord()
    const petsAllowed = or(equalsOne(allow_dogs), equalsOne(allow_cats))
    const description = getDescription()
    const photo = getImageUrl(unitImages[0]?.aws_s3_key, {
      fit: 'fit-in/1200x630',
      quality: 100
    })
    const floorSizeFormat = setting_size_unit === 'meters' ? 'MTK' : ' FTK'
    const listingUrl = `https://liv.rent${getListingUrl()}`
    return JSON.stringify(
      isStreetNumberHidden()
        ? {
            '@context': 'http://schema.org',
            '@type': buildingType,
            url: listingUrl,
            name: getListingName(),
            description: description,
            geo: {
              '@type': 'GeoCoordinates',
              latitude: lat,
              longitude: lng
            },
            containedInPlace: {
              '@type': 'City',
              url: `https://liv.rent/rental-listings/city/${city?.toLowerCase()}`,
              name: city,
              address: {
                '@type': 'PostalAddress',
                addressCountry: country_code,
                addressLocality: city,
                addressRegion: state
              }
            },
            containsPlace: {
              '@type': listingType,
              name: full_address,
              occupancy: max_occupancy,
              floorSize: `${formatSize(
                setting_size_unit,
                unit.size
              )}${floorSizeFormat}`,
              petsAllowed: petsAllowed,
              potentialAction: {
                '@type': 'RentAction',
                landlord: `${landlord.first_name} ${landlord.last_name}`,
                agent: unit.company_name,
                priceSpecification: {
                  '@type': 'PriceSpecification',
                  price: price,
                  priceCurrency: 'CAD'
                }
              }
            },
            address: {
              '@type': 'PostalAddress',
              addressCountry: country_code,
              addressLocality: city,
              addressRegion: state,
              streetAddress: street_name
            },
            image: {
              '@type': 'ImageObject',
              contentUrl: photo
            }
          }
        : {
            '@context': 'http://schema.org',
            '@type': buildingType,
            url: listingUrl,
            name: getListingName(),
            description: description,
            geo: {
              '@type': 'GeoCoordinates',
              latitude: lat,
              longitude: lng
            },
            containedInPlace: {
              '@type': 'City',
              url: `https://liv.rent/rental-listings/city/${city?.toLowerCase()}`,
              name: city,
              address: {
                '@type': 'PostalAddress',
                addressCountry: country_code,
                addressLocality: city,
                addressRegion: state
              }
            },
            containsPlace: {
              '@type': listingType,
              name: full_address,
              occupancy: max_occupancy,
              floorSize: `${formatSize(
                setting_size_unit,
                unit.size
              )}${floorSizeFormat}`,
              petsAllowed: petsAllowed,
              potentialAction: {
                '@type': 'RentAction',
                landlord: `${landlord.first_name} ${landlord.last_name}`,
                agent: unit.company_name,
                priceSpecification: {
                  '@type': 'PriceSpecification',
                  price: price,
                  priceCurrency: 'CAD'
                }
              }
            },
            address: {
              '@type': 'PostalAddress',
              addressCountry: country_code,
              addressLocality: city,
              addressRegion: state,
              postalCode: zip,
              streetAddress: full_street_name
            },
            image: {
              '@type': 'ImageObject',
              contentUrl: photo
            }
          }
    )
  }
  const prefixUnitNumber = () => {
    if (!unit.unit_number) return ''
    return unit.unit_number
  }
  const getUnitNumber = () => {
    return equalsOne(unit.hide_unit_number) ? null : prefixUnitNumber()
  }
  const getAddress = () => {
    const { full_street_name, street_name, city, state } = building
    const baseAddress = isStreetNumberHidden()
      ? `${street_name}, ${city}, ${state}`
      : `${full_street_name}, ${city}, ${state}`
    const unitNumber = getUnitNumber()
    return `${unitNumber ? `${unitNumber} - ` : ''}${baseAddress}`
  }
  const isGroup = () => {
    return isGroupGlobal(unit)
  }
  const isW1 = () => {
    return isW1Global(building.id)
  }
  const buildingAmenities = () => {
    return map(a => ({ txt_id: a }), keys(building.building_amenities))
  }
  const isFurnished = () => {
    if (listings.furnished === null)
      return t('listing:common.furnishings_available', 'Furnish Available')
    return equalsOne(listings.furnished)
      ? t('common.furnished', 'Furnished')
      : t('common.unfurnished', 'Unfurnished')
  }
  const smokingAllowed = () => {
    return equalsOne(listings.allow_smoking)
      ? t('listing:b.listing.view.allowed.text', 'Allowed')
      : t('listing:b.listing.view.no_smoking.text', 'No smoking')
  }

  const renderReportListing = () => {
    const data = {
      listingId: listings.id,
      buildingId: building.id,
      targetUserId: listings.listing_landlords[0]
    }

    const onReport = () =>
      session.id
        ? showReportListingModal({ data, reporterUserId: session.id })
        : showLoginModal({
            mode: 'login',
            title: t(
              'listing:b.listing.view.report_modal.description',
              'Log in to report this listing'
            ),
            eventId: 'web-signup-submit-report-listing',
            t
          })

    return (
      <ReportListingButton onClick={onReport} id="listing-detail-report-click">
        <ClickWrapper>
          <FlagIcon width={16} />
          {t(
            'listing:b.listing.view.report_listing.button.text',
            'Report this listing'
          )}
        </ClickWrapper>
      </ReportListingButton>
    )
  }

  const logout = async () => await dispatch.auth.logout()
  const showAppointmentForm = () => {
    const landlord = getLandlord()
    const hasSession = !!session?.id && session.registration_complete === '1'

    showAppointmentFormModal({
      appointments,
      listingId: listings.id,
      landlordName: landlord.preferred_name || landlord.first_name,
      hasSession,
      logout
    })
  }

  const handleApply = e => {
    e.preventDefault()
    e.stopPropagation()

    window.dataLayer.push({
      event: applyClickEventId
    })

    if (isLandlord) {
      return showModal('landlordWarning')()
    }

    if (isListingLocked) {
      return ensureLoggedIn(() =>
        showModalForRequiredTerms('trreb_agreements', () =>
          window.open(`${DASHBOARD_URL}/application/create/${listings.id}`)
        )
      )
    }

    if (isLoggedIn) {
      return window.open(`${DASHBOARD_URL}/application/create/${listings.id}`)
    }

    return showModal('apply')()
  }

  const landlord = getLandlord()
  const isListingVerified = listings.verified_state_machine === 'approved'
  const isLandlordVerified = landlord.identity_verified_state === 'verified'

  const showListingTour = !isEmpty(appointments) || isListingLandlord
  const metaUrl = getCanonicalUrl(getListingUrl(), router.locale)
  const metaImage = getImageUrl(unitImages[0]?.aws_s3_key, {
    fit: 'fit-in/1200x630',
    quality: 100
  })
  const companyData = companies[unit.company_id]
  const isCompanyInfoPresent =
    landlord.company.account_type === 'business' &&
    any(x => !!x)(
      pipe(
        pick([
          'name',
          'address',
          'work_history_properties_managed',
          'work_history_year_started',
          'business_licence_verified_state'
        ]),
        values
      )({ ...landlord, ...landlord.company })
    )
  const isLandlordInfoPresent = !!landlord.about_me || isCompanyInfoPresent

  const handleContactLandlord = () => {
    window.dataLayer.push({
      event: inquireClickEventId
    })

    if (isLandlord) {
      showModal('landlordWarning')()
    } else {
      const isSponsored = unit.sponsored === 1 || unit.sponsored === '1'
      posthog.capture(
        isSponsored
          ? 'sponsored_listing_inquiry_button_click'
          : 'regular_listing_inquiry_button_click'
      )

      showMessageModal({
        id: listings.id,
        city: building.city,
        bedrooms: unit.count_bedrooms,
        bathrooms: unit.count_all_bathrooms,
        allow_applications: Boolean(listings.allow_applications),
        state: building.state,
        street_name: building.street_name,
        full_street_name: building.full_street_name,
        gr_unit: unit.gr_unit,
        gr_min_price: unit.gr_min_price,
        gr_max_price: unit.gr_max_price,
        price: listings.price,
        price_frequency: listings.price_frequency,
        state_machine: listings.state_machine,
        marked_as_rented: listings.marked_as_rented,
        hide_unit_number: unit.hide_unit_number,
        building_type: building.building_type_txt_id,
        sponsored: isSponsored,
        super_boost: listings.super_boost?.value
      })
    }
  }

  let unit_features = unit.unit_features
  //shared bathrooms need to be listed in features
  if (
    unit.unit_type_scope_txt_id !== 'entire' &&
    unit.count_full_shared_bathrooms &&
    parseFloat(unit.count_full_shared_bathrooms) > 0
  ) {
    const dummyBathroomFeature = {
      name: 'Bathroom',
      txt_id: 'bathroom',
      types: {
        private: parseFloat(unit.count_full_bathrooms || 0),
        shared: parseFloat(unit.count_full_shared_bathrooms)
      }
    }

    unit_features = [dummyBathroomFeature, ...unit_features]
  }

  const isFavourite = listings.id in favourites
  const onFavourite = async () => {
    if (isLandlord) {
      showModal('landlordWarning')()
    } else {
      if (isFavourite) {
        const response = await dispatch.listings.removeFavourite(listings.id)
        if (response?.error)
          showToast({ message: response.error.message, tone: 'critical' })
        else
          showToast({
            message: t(
              'favourites:listing_removed',
              'Listing removed from favourites!'
            ),
            action: {
              label: t('favourites:view_favourites', 'View your favourites'),
              onClick: () => {
                window.open(`/favourites`, '_blank')
              }
            },
            tone: 'positive'
          })
      } else {
        const response = await dispatch.listings.favourite(listings.id)

        if (response?.error)
          showToast({ message: response.error.message, tone: 'critical' })
        else
          showToast({
            message: t(
              'favourites:listing_saved',
              'Listing saved to favourites!'
            ),
            action: {
              label: t('favourites:view_favourites', 'View your favourites'),
              onClick: () => {
                window.open(`/favourites`, '_blank')
              }
            },
            tone: 'positive'
          })
      }
    }
  }

  const handleFavouriteClickForUnlockedListing = () => {
    if (isLoggedIn) {
      onFavourite()
    } else {
      setAfterLogin(onFavourite)
      showModal('loginModal')()
    }
  }
  const handleFavourite = async () => {
    window.dataLayer.push({
      event: 'listing-detail-save-click'
    })

    isListingLocked
      ? ensureLoggedIn(() =>
          showModalForRequiredTerms('trreb_agreements', onFavourite)
        )
      : handleFavouriteClickForUnlockedListing()
  }

  const handleShare = () => {
    window.dataLayer.push({
      event: 'listing-detail-share-click'
    })
    showShareModal({
      url: metaUrl,
      t
    })
  }

  const SideCardComponent = isLoggedIn ? TermsRequiredCard : LoginRequiredCard

  const hasSpecialOffer =
    !!listings.listing_deal?.description &&
    listings.listing_deal.description.length > 0

  // temporary measure, need to break away from reusing same
  // template for all responsive layouts.
  const responsiveMargin = isSidePanelHidden
    ? isIpadMini
      ? '0 32px'
      : '0 16px'
    : '0'

  return (
    <>
      <Head
        title={getTitle()}
        description={getDescription()}
        meta={{
          image: metaImage,
          imageAlt: getAddress(),
          schema: getListingSchema(),
          type: 'product',
          price: isGroup() ? unit.gr_min_price : listings.price
        }}
        path={getListingUrl()}
      />
      <ResponsiveRender above="mobile">
        <DesktopHeader listings={listings} handleShare={handleShare} />
      </ResponsiveRender>

      <StatusBanner listings={listings} setShowTopNav={setShowTopNav} />

      <ResponsiveRender above="mobile">
        {isRented && <RentedNotification />}
      </ResponsiveRender>

      {isListingLandlord &&
        isWarningOpen &&
        (!isListingVerified || !isLandlordVerified) && (
          <WarningNotification
            isListingVerified={isListingVerified}
            isLandlordVerified={isLandlordVerified}
            listingId={listings.id}
            closeWarningNotification={() => setIsWarningOpen(false)}
          />
        )}

      <ListingInfo
        ButtonsPlacementComponent={InternalNavBarButtons}
        ButtonsPlacementProps={{}}
        listings={listings}
        showTopNav={showTopNav}
      >
        <ListingInfoContent
          style={{ padding: isSidePanelHidden ? '0' : '0 32px' }}
        >
          <ListingInfoLeft>
            <NavigationSection
              title={t(
                'listing:b.listing.navigation.overview.text',
                'Overview'
              )}
              id="listing-detail-overview-navbar"
            >
              <ListingImages
                listingLinks={listingLinks}
                unitVideos={unitVideos}
                unitImages={unitImages}
                building={building}
                unit={unit}
                unitType={unitType}
                getBedCount={getBedCount}
                isStreetNumberHidden={isStreetNumberHidden}
                isListingLocked={isListingLocked}
                isTabletPortrait={isTabletPortrait}
                isSidePanelHidden={isSidePanelHidden}
                sidePanelBreakpoint={sidePanelBreakpoint}
                responsiveMargin={responsiveMargin}
              />
              <Box
                style={{
                  margin: responsiveMargin,
                  paddingTop: isTabletPortrait ? '8px' : '0px'
                }}
              >
                <Stack space="gutter">
                  <ListingSummary
                    isStreetNumberHidden={isStreetNumberHidden()}
                    cityName={building.city}
                    furnished={isFurnished()}
                    listings={listings}
                    listingLinks={listingLinks}
                    unitVideos={unitVideos}
                    building={building}
                    unit={unit}
                    isFavourite={isFavourite}
                    handleFavourite={handleFavourite}
                    handleShare={handleShare}
                    areaMeasurementUnit={session?.setting_size_unit}
                    isListingLocked={isListingLocked}
                  />

                  {isSidePanelHidden && hasSpecialOffer && (
                    <SpecialOffer deal={listings.listing_deal} />
                  )}

                  <ListingContact
                    listings={listings}
                    landlord={landlord}
                    isLandlordInfoPresent={isLandlordInfoPresent}
                    companyData={companyData}
                    isListingLocked={isListingLocked}
                    externalInfo={listings.external_info}
                  />

                  {showListingTour && (
                    <ListingTour
                      listings={listings}
                      landlord={landlord}
                      isListingLandlord={isListingLandlord}
                      appointments={appointments}
                      onAppointmentClick={showAppointmentForm}
                    />
                  )}

                  {!isDesktop && !isLoggedIn && isImported ? (
                    <LoginRequiredCard externalInfo={listings.external_info} />
                  ) : !isDesktop && isAgreementMismatched && isImported ? (
                    <TermsRequiredCard externalInfo={listings.external_info} />
                  ) : null}

                  {isListingLocked ? (
                    <LeaseDetailsBlurred listings={listings} unit={unit} />
                  ) : (
                    <LeaseDetails listings={listings} unit={unit} />
                  )}
                  {isSidePanelHidden && allowApplications ? (
                    <Box width="full">
                      <Button
                        onClick={handleApply}
                        id={applyClickEventId}
                        variant="ghost"
                      >
                        {t('Start Your Application')}
                      </Button>
                    </Box>
                  ) : (
                    <Box
                      style={{ height: '40px', marginBottom: '40px' }}
                      borderBottom="borderNeutralLight"
                    />
                  )}
                </Stack>
              </Box>
            </NavigationSection>

            <Box style={{ margin: responsiveMargin, paddingTop: '16px' }}>
              <ListingDetailsTitle>
                {t(
                  'listing:b.listing.view.listing_details.title',
                  'Listing Details'
                )}
              </ListingDetailsTitle>
              <ListingDescription
                description={listings?.description}
                isListingLocked={isListingLocked}
              />
            </Box>

            <NavigationSection
              title={t(
                'listing:b.listing.navigation.amenities.text',
                'Amenities'
              )}
              id="listing-detail-amenity-navbar"
            >
              <Box style={{ margin: responsiveMargin }}>
                <ListingAmenities
                  isEntireUnit={unit?.unit_type_scope_txt_id === 'entire'}
                  unitFeatures={unit_features}
                  customFeatures={listings?.custom_features}
                  unitUtilities={listings?.listing_utilities}
                  customUtilities={listings?.custom_utilities}
                  building={buildingAmenities()}
                  buildingUrl={buildingUrl}
                  isHouse={isHouse()}
                  smoking={smokingAllowed()}
                  pets={{
                    allowed: listings?.allow_pets,
                    dogs: listings?.allow_dogs,
                    cats: listings?.allow_cats
                  }}
                  blurred={isListingLocked}
                />
              </Box>
            </NavigationSection>
            <Box style={{ margin: responsiveMargin }}>
              <Stack space="large">
                {isApartmentBuilding && (
                  <Stack space="xxxlarge">
                    <NavigationSection
                      title={t(
                        'listing:b.listing.navigation.building.text',
                        'Building'
                      )}
                      id="listing-detail-building-navbar"
                    >
                      <StyledListingSection
                        title={t(
                          'listing:b.listing.view.building_details.text',
                          'Building Details'
                        )}
                        titleMargin="0"
                      >
                        <BuildingInfo
                          data={building}
                          buildingImages={buildingImages}
                          buildingUrl={buildingUrl}
                          isListingLocked={isListingLocked}
                        />
                      </StyledListingSection>
                    </NavigationSection>
                  </Stack>
                )}
                <Box style={{ marginBottom: '16px' }}>
                  {renderReportListing()}
                </Box>
              </Stack>
            </Box>
          </ListingInfoLeft>

          <ListingInfoRight>
            <Stack space="gutter">
              {hasSpecialOffer && (
                <Box>
                  <SpecialOffer deal={listings.listing_deal} />
                </Box>
              )}
              {isListingLocked ? (
                <Box>
                  <SideCardComponent externalInfo={listings?.external_info} />
                </Box>
              ) : (
                <Box>
                  <Card rounded>
                    <Stack space="medium">
                      <Text weight="strong" size="medium">
                        {t('Like this listing?')}
                      </Text>
                      <Text size="small">
                        {t(
                          'Begin your exciting journey now to make your dream home a beautiful reality!'
                        )}
                      </Text>
                      <Box marginY="xsmall">
                        <Button
                          theme="blueSecondary"
                          width="full"
                          id={inquireClickEventId}
                          onClick={handleContactLandlord}
                        >
                          {t('Check Availability')}
                        </Button>
                      </Box>
                      {allowApplications && (
                        <Text size="small">
                          {t('Ready to apply?')}{' '}
                          <TextLink
                            id={applyClickEventId}
                            onClick={handleApply}
                            style={{
                              textDecoration: 'underline',
                              color: colors.link
                            }}
                          >
                            {t('Complete Your Application')}
                          </TextLink>
                        </Text>
                      )}
                    </Stack>
                  </Card>
                </Box>
              )}
            </Stack>
          </ListingInfoRight>
        </ListingInfoContent>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          width="full"
          background="neutralLight"
          paddingX={{ mobile: 'small', tablet: 'large' }}
          style={{ overflow: 'hidden' }}
        >
          <StyledNavigationSection
            title={t(
              'listing:b.listing.navigation.neighbourhoods.text',
              'Neighbourhoods'
            )}
            id="listing-detail-poi-neighbourhoods-navbar"
          >
            <PointsOfInterest
              serverSidePoints={poiData}
              buildingId={building.id}
              listing={{
                name: building.name,
                address: building.full_street_name,
                lat: building.lat,
                lng: building.lng
              }}
              isStreetNumberHidden={isStreetNumberHidden()}
              isListingLocked={isListingLocked}
            />
          </StyledNavigationSection>

          {similarListings && <SimilarListings data={similarListings} />}
          {citySlug && (
            <Box
              width="full"
              style={{
                maxWidth: `${structure.page.width}px`
              }}
            >
              <CityBreadcrumbs cityData={cityData} loading={cityDataLoading} />
            </Box>
          )}
          <CityLinkList />
        </Box>
      </ListingInfo>
      {isW1() && renderDisclaimer(unit.company_name)}

      {!isLoggedIn && isImported && (
        <TabletApplyFooterWrapper $isIpadMini={isIpadMini}>
          <TabletApplyFooterCenter>
            <Box paddingY="small">
              <Stack space="small">
                <Text weight="strong" size="xlarge">
                  {t('b.listing.login.required.card.title', 'Login Required')}
                </Text>

                <Trans
                  i18nKey="b.listing.login.required.card.action.text"
                  t={t}
                >
                  <Text>
                    Please{' '}
                    <TextLinkButton
                      weight="regular"
                      id="listing_details_mls_loginrequired_login_click"
                      onClick={() =>
                        showLoginModal({
                          mode: 'login',
                          eventId: 'web-signup-submit-login-redirect-mls',
                          t
                        })
                      }
                    >
                      login
                    </TextLinkButton>{' '}
                    or{' '}
                    <TextLinkButton
                      weight="regular"
                      id="listing_details_mls_loginrequired_signup_click"
                      onClick={() =>
                        showLoginModal({
                          mode: 'register',
                          eventId: 'web-signup-submit-login-redirect-mls',
                          t
                        })
                      }
                    >
                      sign up
                    </TextLinkButton>{' '}
                    to continue.
                  </Text>
                </Trans>
              </Stack>
            </Box>
          </TabletApplyFooterCenter>
        </TabletApplyFooterWrapper>
      )}
      {isLoggedIn && isImported && isAgreementMismatched && (
        <TabletApplyFooterWrapper $isIpadMini={isIpadMini}>
          <TabletApplyFooterCenter>
            <TabletApplyFooterContainer>
              <StickyContentWrapper>
                <Text weight="strong" size="xlarge">
                  {t('b.listing.terms.required.card.title', 'Terms Required')}
                </Text>
              </StickyContentWrapper>
              <TabletButtonWrapper>
                {allowApplications && (
                  <Button
                    icon={<IconArrowUpRight />}
                    id="listing_details_mls_termsrequired_taptoview_click"
                    onClick={() =>
                      showModalForRequiredTerms('trreb_agreements', () => {})
                    }
                    variant="ghost"
                    size="small"
                  >
                    {t(
                      'b.listing.terms.required.card.button.text',
                      'Tap to view'
                    )}
                  </Button>
                )}
              </TabletButtonWrapper>
            </TabletApplyFooterContainer>
          </TabletApplyFooterCenter>
        </TabletApplyFooterWrapper>
      )}

      {!isListingLocked && (
        <TabletApplyFooterWrapper $isIpadMini={isIpadMini}>
          <TabletApplyFooterCenter>
            <Box
              display="flex"
              width="full"
              paddingY="small"
              alignItems="center"
              justifyContent="center"
            >
              <Button
                id={inquireClickEventId}
                onClick={handleContactLandlord}
                disabled={isRented}
              >
                {t(
                  'b.appointment.modal.check_availability.button',
                  'Check Availability'
                )}
              </Button>
            </Box>
          </TabletApplyFooterCenter>
        </TabletApplyFooterWrapper>
      )}
      <WarningModal
        visible={visible.landlordWarning}
        closeModal={closeModal('landlordWarning')}
        title={t(
          'b.listing.view.logged_in_as_landlord.title',
          "Oops! It looks like you're logged in as a landlord."
        )}
        description={
          <Box marginTop="small">
            <Text tone="secondary" align="center" size="medium">
              <Trans i18nKey="b.listing.view.logged_in_as_landlord.text">
                To access renter-specific features, please log out and sign up
                for a renter account or log in with your renter account details.
                <br />
                <br />
                If you made this account in error, you can contact us at{' '}
                <TextLink
                  href="mailto:support@liv.rent"
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  support@liv.rent
                </TextLink>
                .
              </Trans>
            </Text>
          </Box>
        }
        button={{
          text: t('common.log_out', 'Log Out'),
          onClick: () => {
            logout()
            closeModal('landlordWarning')()
          }
        }}
      />
      <DSLoginModal
        visible={visible.loginModal}
        onClose={closeModal('loginModal')}
        t={t}
      />
      <ApplyNowStepModal
        visible={visible.apply}
        onClose={closeModal('apply')}
        showModal={showModal('apply')}
        listingId={listings.id}
        eventId="details-page"
      />
    </>
  )
}

export default ListingDetailPage
